import React, { useEffect, useState, useCallback } from "react";

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, Request } from "types";

import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import {
  CheckCircle,
  DeleteOutline,
  InfoOutlined,
  Schedule,
  Warning,
  AccessTime,
} from "@mui/icons-material";

import DB from "_DatabaseFunctions/directConnect";
import CALL from "_DatabaseFunctions/cloudCalls";

import { formatDate } from "utils/helperFunctions";

interface RequestWithDetails extends Request {
  gameName: string;
  gameId: string;
}

const UserRequests = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const requests = useSelector((state: RootState) => state.requests.sentRequests);
  const user = useSelector((state: RootState) => state.user.user);
  const [currentTime, setCurrentTime] = useState<number>(Date.now());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timerID = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    
    return () => {
      clearInterval(timerID);
    };
  }, []);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'pending':
        return 'info';
      case 'waitlist':
        return 'warning';
      case 'confirmationPending':
        return 'error';
      case 'hostConfirmed':
        return 'success';
      case 'playerConfirmed':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmationPending':
        return <Warning fontSize="small" />;
      case 'hostConfirmed':
      case 'playerConfirmed':
        return <CheckCircle fontSize="small" />;
      default:
        return <AccessTime fontSize="small" />;
    }
  };

  const formatCountdown = useCallback((timeStamp: number, reqId: string): string => {
    const timeRemaining = timeStamp - currentTime;
    
    if (timeRemaining < 0) {
      // Auto-delete expired requests
      DB.deleteRequest(reqId);
      return '0m 0s';
    }
    
    const totalSeconds = Math.floor(timeRemaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    return `${minutes}m ${seconds}s`;
  }, [currentTime]);

  const StatusMap: Record<string, string> = {
    pending: "Pending",
    waitlist: "On Waitlist",
    confirmationPending: "Confirmation Needed",
    hostConfirmed: "Host Confirmed Seat",
    playerConfirmed: "In Game, Seat Confirmed",
  };

  const handleConfirmJoin = async (reqData: RequestWithDetails) => {
    if (!user || !reqData) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { gameName, gameId, senderId } = reqData;
      
      await Promise.all([
        DB.confirmAddPlayer(gameId, user.uid),
        DB.playerConfirmReq(reqData.id),
        DB.sendPlayerConfirmedNotification(reqData),
        DB.startPlayerSession({
          reqId: reqData.id,
          playerId: user.uid,
          playerName: user.displayName || '',
          gameId,
          gameName,
          hostId: senderId,
        }),
      ]);
      
      // Send notifications asynchronously
      CALL.sendPlayerConfirmedEmail(reqData);
      CALL.sendPlayerConfirmedText(reqData);
    } catch (err) {
      console.error('Error confirming join:', err);
      setError('Failed to confirm joining the game. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (reqData: RequestWithDetails) => {
    if (!user || !reqData) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { status, gameId, id } = reqData;
      
      const operations = [];
      
      if (status === "waitlist") {
        operations.push(DB.removeFromWaitlist(gameId, user.uid));
      } else if (status === "confirmationPending" || status === "hostConfirmed") {
        operations.push(
          DB.declineJoinGame(gameId, user.uid),
          DB.sendPlayerDeclinedNotification(reqData)
        );
        // Send notifications asynchronously
        CALL.sendPlayerDeclinedEmail(reqData);
      } else if (status === "playerConfirmed") {
        operations.push(
          DB.removePlayerFromGame(gameId, user.uid),
          DB.endPlayerSession({ gameId, playerId: user.uid })
        );
      }
      
      operations.push(DB.deleteRequest(id));
      await Promise.all(operations);
    } catch (err) {
      console.error('Error deleting request:', err);
      setError('Failed to delete request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!requests) {
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: 200,
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress 
            color="primary" 
            size={40}
            thickness={4}
          />
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            Loading requests...
          </Typography>
        </Box>
      </Box>
    );
  }

  const requestsList = Object.values(requests) as RequestWithDetails[];
  
  if (requestsList.length === 0) {
    return (
      <Box>
        <Box sx={{ 
          textAlign: 'center', 
          py: 6,
          px: 3,
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2
        }}>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            No requests found
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ opacity: 0.8 }}
          >
            You haven't sent any game requests yet. Browse games and send requests to join!
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2,
          mb: 2
        }}
      >
        <Typography 
          variant="h6"
          sx={{ 
            fontWeight: 600,
            color: 'text.primary'
          }}
        >
          Your Requests
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <TableContainer
          sx={{
            maxHeight: isMobile ? 400 : 600,
            '& .MuiTableCell-root': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            }
          }}
        >
          <Table 
            stickyHeader 
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiTableHead-root': {
                '& .MuiTableCell-root': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontWeight: 600,
                  borderBottom: 'none'
                }
              },
              '& .MuiTableBody-root': {
                '& .MuiTableRow-root': {
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&.highlight': {
                    bgcolor: 'warning.light',
                    '&:hover': {
                      bgcolor: 'warning.main',
                    },
                  },
                },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Game Name</TableCell>
                <TableCell>Time / Countdown</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requestsList.map((reqData) => {
                const isPending = reqData.status === "confirmationPending";
                const needsConfirmation = isPending || reqData.status === "hostConfirmed";
                
                return (
                  <TableRow 
                    key={reqData.id} 
                    className={isPending ? "highlight" : ""}
                  >
                    <TableCell
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'primary.main',
                          textDecoration: 'underline',
                        },
                      }}
                      onClick={() => navigate(`/games/${reqData.gameId}`)}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {reqData.gameName}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      {isPending ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                          <Schedule fontSize="small" />
                          <Typography variant="body2" fontWeight="bold">
                            {formatCountdown(reqData.timeStamp, reqData.id)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          {formatDate(reqData.timeStamp)}
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(reqData.status)}
                        label={StatusMap[reqData.status] || reqData.status}
                        color={getStatusColor(reqData.status)}
                        variant={isPending ? "filled" : "outlined"}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {needsConfirmation && (
                          <Tooltip title="Confirm and Join Game">
                            <span>
                              <IconButton
                                onClick={() => handleConfirmJoin(reqData)}
                                disabled={loading}
                                size="small"
                                sx={{
                                  bgcolor: 'success.light',
                                  color: 'white',
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    bgcolor: 'success.main',
                                  },
                                  '&:disabled': {
                                    bgcolor: 'grey.300',
                                    color: 'grey.500',
                                  }
                                }}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Delete Request">
                          <span>
                            <IconButton
                              onClick={() => handleDeleteRequest(reqData)}
                              disabled={loading}
                              size="small"
                              sx={{
                                bgcolor: 'error.light',
                                color: 'white',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  bgcolor: 'error.main',
                                },
                                '&:disabled': {
                                  bgcolor: 'grey.300',
                                  color: 'grey.500',
                                }
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        
                        <Tooltip title="Game Information">
                          <span>
                            <IconButton
                              onClick={() => navigate(`/games/${reqData.gameId}`)}
                              size="small"
                              sx={{
                                bgcolor: 'info.light',
                                color: 'white',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  bgcolor: 'info.main',
                                },
                                '&:disabled': {
                                  bgcolor: 'grey.300',
                                  color: 'grey.500',
                                }
                              }}
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
      </TableContainer>
    </Box>
  );
};

export default UserRequests;